/**
 * Offline Queue — R53.31
 *
 * Coda di azioni HACCP salvate localmente in IndexedDB quando il dispositivo
 * è offline. Vengono drenate automaticamente al ritorno della connessione.
 *
 * Architettura minimal:
 *  - 1 DB IndexedDB "haccp_offline" v1
 *  - 1 object store "ops" (keyPath: local_id)
 *  - Eventi pub/sub locali per aggiornare la UI (banner contatore)
 *
 * Schema operazione:
 *  {
 *    local_id: string (uuid),
 *    ts:       number (epoch ms),
 *    method:   "POST" | "PUT" | "DELETE",
 *    url:      "/temperature",
 *    body:     object | null,
 *    label:    string (per UI),
 *    tries:    number,
 *    last_error: string | null,
 *  }
 *
 * Non gestiamo upload file (foto/DDT) intenzionalmente: richiedono rete e
 * sono extra-Plus. La coda copre i dati testuali quotidiani HACCP.
 */

const DB_NAME = "haccp_offline";
const DB_VERSION = 1;
const STORE = "ops";

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB non disponibile"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "local_id" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("openDB failed"));
  });
  return _dbPromise;
}

function tx(mode) {
  return openDB().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // fallback
  return "op-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

// ── Pub/Sub per la UI ──
const _listeners = new Set();
function notify() {
  _listeners.forEach((fn) => {
    try { fn(); } catch { /* ignore */ }
  });
}

export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

// Listener dedicati per ricevere il risultato del drain (auto o manuale).
// Usato dal banner per mostrare lo stato verde "Sincronizzate N azioni".
const _drainListeners = new Set();
function notifyDrainResult(res) {
  _drainListeners.forEach((fn) => {
    try { fn(res); } catch { /* ignore */ }
  });
}

export function subscribeDrainResult(fn) {
  _drainListeners.add(fn);
  return () => _drainListeners.delete(fn);
}

// ── API pubblica ──

export async function enqueue({ method, url, body = null, label = "" }) {
  const op = {
    local_id: uuid(),
    ts: Date.now(),
    method: (method || "POST").toUpperCase(),
    url,
    body,
    label: label || `${method} ${url}`,
    tries: 0,
    last_error: null,
  };
  try {
    const store = await tx("readwrite");
    await new Promise((res, rej) => {
      const r = store.add(op);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    });
    notify();
    return op;
  } catch (e) {
    // Quota o DB rotto: fallback memoria volatile (mai persa per crash app)
    // ma almeno non perde la singola azione.
    console.error("[offlineQueue] enqueue failed", e);
    throw e;
  }
}

export async function listAll() {
  try {
    const store = await tx("readonly");
    return await new Promise((res, rej) => {
      const r = store.getAll();
      r.onsuccess = () => res(r.result || []);
      r.onerror = () => rej(r.error);
    });
  } catch {
    return [];
  }
}

export async function count() {
  const items = await listAll();
  return items.length;
}

export async function remove(local_id) {
  try {
    const store = await tx("readwrite");
    await new Promise((res, rej) => {
      const r = store.delete(local_id);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    });
    notify();
  } catch (e) {
    console.error("[offlineQueue] remove failed", e);
  }
}

async function update(op) {
  try {
    const store = await tx("readwrite");
    await new Promise((res, rej) => {
      const r = store.put(op);
      r.onsuccess = () => res();
      r.onerror = () => rej(r.error);
    });
    notify();
  } catch (e) {
    console.error("[offlineQueue] update failed", e);
  }
}

/**
 * Drena la coda inviando ciascuna operazione al backend.
 *
 * @param {AxiosInstance} api - istanza axios autenticata
 * @returns {Promise<{ok: number, fail: number}>}
 */
let _draining = false;
export async function drain(api) {
  if (_draining) return { ok: 0, fail: 0, skipped: true };
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { ok: 0, fail: 0, offline: true };
  }
  _draining = true;
  let ok = 0;
  let fail = 0;
  try {
    const items = await listAll();
    // FIFO per timestamp
    items.sort((a, b) => (a.ts || 0) - (b.ts || 0));
    for (const op of items) {
      try {
        const method = (op.method || "POST").toLowerCase();
        if (method === "post") await api.post(op.url, op.body);
        else if (method === "put") await api.put(op.url, op.body);
        else if (method === "delete") await api.delete(op.url);
        else throw new Error(`Metodo non supportato: ${op.method}`);
        await remove(op.local_id);
        ok += 1;
      } catch (err) {
        // Se è ancora un errore di rete, fermati: probabile flap, riproveremo
        const isNet = !err?.response;
        if (isNet) {
          fail += 1;
          break;
        }
        // Errore HTTP (4xx/5xx): segna ma non bloccare la coda
        op.tries = (op.tries || 0) + 1;
        op.last_error = `${err?.response?.status || "?"} ${
          err?.response?.data?.detail || err.message || "errore"
        }`;
        // Dopo 3 tentativi falliti con 4xx, rimuoviamo (il payload è invalido)
        if (op.tries >= 3 && err?.response?.status && err.response.status >= 400 && err.response.status < 500) {
          await remove(op.local_id);
        } else {
          await update(op);
        }
        fail += 1;
      }
    }
  } finally {
    _draining = false;
    notify();
  }
  const result = { ok, fail };
  notifyDrainResult(result);
  return result;
}

/**
 * Inizializza i listener globali: drain automatico quando torna online.
 * Chiamare 1 sola volta dall'app (es. in App.js o lib/api.js bootstrap).
 */
let _initialized = false;
export function initAutoDrain(api) {
  if (_initialized || typeof window === "undefined") return;
  _initialized = true;
  const tryDrain = () => {
    drain(api).catch(() => {});
  };
  window.addEventListener("online", tryDrain);
  // Tentativo iniziale al boot (potrebbero esserci op rimaste dalla sessione precedente)
  setTimeout(tryDrain, 1500);
  // Re-tentativo periodico ogni 60s se ci sono operazioni in coda
  setInterval(async () => {
    const n = await count();
    if (n > 0 && navigator.onLine) tryDrain();
  }, 60_000);
}
